const GENERATED_REPORT_COLUMNS_PREFIX = "generatedReportColumns:";
const GENERATED_REPORT_COLUMNS_INDEX_KEY = `${GENERATED_REPORT_COLUMNS_PREFIX}index`;
const MAX_SAVED_REPORT_COLUMN_CONFIGS = 120;
const MAX_CONFIG_AGE_MS = 1000 * 60 * 60 * 24 * 15;

type ColumnVisibilityIndex = Record<string, number>;

const parseJsonSafely = (value: string | null) => {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const loadIndex = (): ColumnVisibilityIndex => {
  const parsed = parseJsonSafely(
    localStorage.getItem(GENERATED_REPORT_COLUMNS_INDEX_KEY),
  );

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }

  return parsed as ColumnVisibilityIndex;
};

const saveIndex = (indexMap: ColumnVisibilityIndex) => {
  localStorage.setItem(
    GENERATED_REPORT_COLUMNS_INDEX_KEY,
    JSON.stringify(indexMap),
  );
};

const removeKey = (storageKey: string, indexMap: ColumnVisibilityIndex) => {
  localStorage.removeItem(storageKey);
  delete indexMap[storageKey];
};

const cleanupOldEntries = (indexMap: ColumnVisibilityIndex, now: number) => {
  const knownEntries = Object.entries(indexMap).filter(([key]) =>
    key.startsWith(GENERATED_REPORT_COLUMNS_PREFIX) &&
    key !== GENERATED_REPORT_COLUMNS_INDEX_KEY,
  );

  knownEntries.forEach(([key, updatedAt]) => {
    const lastUpdated = Number(updatedAt) || 0;
    const isExpired = now - lastUpdated > MAX_CONFIG_AGE_MS;
    const isMissingFromStorage = localStorage.getItem(key) === null;

    if (isExpired || isMissingFromStorage) {
      removeKey(key, indexMap);
    }
  });

  const remainingEntries = Object.entries(indexMap)
    .filter(
      ([key]) =>
        key.startsWith(GENERATED_REPORT_COLUMNS_PREFIX) &&
        key !== GENERATED_REPORT_COLUMNS_INDEX_KEY,
    )
    .sort((a, b) => Number(a[1] || 0) - Number(b[1] || 0));

  const overflow = remainingEntries.length - MAX_SAVED_REPORT_COLUMN_CONFIGS;
  if (overflow <= 0) return;

  remainingEntries.slice(0, overflow).forEach(([key]) => {
    removeKey(key, indexMap);
  });
};

export const readVisibleColumnsFromStorage = (storageKey: string) => {
  try {
    const parsed = parseJsonSafely(localStorage.getItem(storageKey));
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveVisibleColumnsToStorage = (
  storageKey: string,
  columns: unknown[],
) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(columns));

    const now = Date.now();
    const indexMap = loadIndex();
    indexMap[storageKey] = now;

    cleanupOldEntries(indexMap, now);
    saveIndex(indexMap);
  } catch {
    // noop: storage can fail in private mode/quota limits; UI state still updates
  }
};
